import { memo, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../../components/BodyCopy";
import { AuraLayout, Button, Layout, PageTitle } from "lola-framework-ui-test";
import {
  StepsCarousel,
  type CarouselStep,
} from "../../components/lola/StepsCarousel";
import { useStore } from "../../store/globalState";
import { useGalleryStore } from "../../gallery/galleryStore";
import { accentInkOnSurface } from "../../utils/accentInkOnSurface";
import { APP_ROUTES, FUND_REQUEST_ROUTES, TITLE_COLOR } from "../../constants";
import {
  CASH_DEPOSIT_AMOUNT,
  CASH_DEPOSIT_CURRENCY,
} from "../../gallery/cashDepositMock";
import { formatLabeledAmount } from "../../utils";
import stepDepositTap from "../../assets/cash_deposit_how_to/step-deposit-tap.svg?raw";
import step2 from "../../assets/cash_deposit_how_to/step2.svg?raw";
import step3 from "../../assets/cash_deposit_how_to/step3.svg?raw";
import step4 from "../../assets/cash_deposit_how_to/step4.svg?raw";
import step5 from "../../assets/cash_deposit_how_to/step5.svg?raw";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";


const ILLUSTRATIONS = [stepDepositTap, step2, step3, step4, step5];

/** Steps that quote the exact phrase to say at the counter. */
const SCRIPTED_STEPS = new Set(["3"]);

const STEP_KEYS = ["1", "2", "3", "4", "5"] as const;

const CASH_TO_BRING = formatLabeledAmount(
  CASH_DEPOSIT_AMOUNT,
  CASH_DEPOSIT_CURRENCY,
);

/**
 * Gallery-safe post-cash carousel: tap Deposit on Transactions, then in-store steps.
 * Got it → transactions; store locator is informational only.
 */
export const HowToDepositCashPage = memo(() => {
  const { t } = useTranslation("fund-request");
  const { theme } = useStore();
  const partnerColors = useGalleryStore(
    (s) => s.rawTheme.colors as Record<string, string>,
  );
  const inactiveColor = partnerColors.inactived;
  const colorConfig = theme.whiteView;
  const {
    background,
    backgroundBtn,
    textColorBtn,
    bodyCopy,
    footerColor,
    title,
  } = colorConfig;
  const navigate = useNavigate();

  const steps = useMemo<CarouselStep[]>(
    () =>
      STEP_KEYS.map((key, position) => ({
        id: key,
        artClassName: key === "1" ? "cdh-slide-art--deposit-tap" : undefined,
        illustration: ILLUSTRATIONS[position],
        title: t(`howToDeposit.steps.${key}.title`),
        body: t(`howToDeposit.steps.${key}.body`, { amount: CASH_TO_BRING }),
        script: SCRIPTED_STEPS.has(key)
          ? t(`howToDeposit.steps.${key}.script`)
          : undefined,
      })),
    [t],
  );

  const accentSolid = accentInkOnSurface(partnerColors, background, title);

  const themeVars = {
    "--cdh-ink": accentSolid,
    "--cdh-mid": `color-mix(in srgb, ${accentSolid} 55%, ${background})`,
    "--cdh-soft": `color-mix(in srgb, ${accentSolid} 22%, ${background})`,
    "--cdh-fill": `color-mix(in srgb, ${accentSolid} 10%, ${background})`,
    "--cdh-neutral": inactiveColor ?? footerColor,
    "--cdh-neutral-strong": footerColor,
    "--cdh-white": background,
    "--cdh-title": accentSolid,
    "--cdh-body": bodyCopy,
    "--cdh-muted": footerColor,
    "--cdh-accent": accentSolid,
    "--cdh-surface": background,
    "--cdh-border": inactiveColor ?? bodyCopy,
  } as React.CSSProperties;

  return (
    <AuraLayout colorConfig={colorConfig} className="cdh-page">
      <Layout.Content className="cdh-section" style={themeVars}>
        <ScrollFadeIndicator color={bodyCopy} />
        <section className="cdh-hero">
          <PageTitle
            highlight={t("howToDeposit.titleHighlight")}
            highlightColor={TITLE_COLOR}
            secudnary={t("howToDeposit.titleSecondary")}
            secudnaryColor={TITLE_COLOR}
          />
        </section>

        <StepsCarousel
          steps={steps}
          regionLabel={t("howToDeposit.title")}
          stepLabel={(position, total) =>
            t("howToDeposit.stepOf", { current: position + 1, total })
          }
          goToLabel={(position) =>
            t("howToDeposit.goToStep", { step: position + 1 })
          }
        />
      </Layout.Content>

      <Layout.Footer style={themeVars}>
        <section className="footer-section cdh-footer">
          <BodyCopy textColor={footerColor} className="cdh-store-count">
            {t("howToDeposit.storeCount")}
          </BodyCopy>
          <Button
            type="button"
            size="large"
            variant="outline"
            className="cdh-secondary-cta"
            background={backgroundBtn}
            color={accentSolid}
            style={{ backgroundColor: background }}
            onClick={() =>
              navigate(`${APP_ROUTES.STORES_MAP_PAGE}?from=fund-request`)
            }
          >
            {t("howToDeposit.findStores")}
          </Button>
          <Button
            type="button"
            size="large"
            className="lola-return-chat"
            background={backgroundBtn}
            color={textColorBtn}
            onClick={() => navigate(FUND_REQUEST_ROUTES.TRANSACTIONS)}
          >
            {t("howToDeposit.cta")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
